import { PRODUCT_RECORDS_PATH } from "@/pages/analytics/product/productRecordsTabs";

export const ANALYTICS_PATH = "/analytics";

/** One Analytics sidebar child: an analytics screen and the route it lives at. */
export type AnalyticsChild = {
  /** The sidebar label and page title, in full (no short forms). */
  label: string;
  /** The child's route. Its sub-routes, such as Product records' tabs, live under it. */
  path: string;
  /** The delivery ledger's screen id. Product records predates the SCR ids, so it has none. */
  screenId?: `SCR-${number}`;
  /**
   * True once `src/common/routes/index.tsx` registers the route. The sidebar lists only shipped
   * children, and `test/unit/analytics-menu.test.ts` fails when this flag and the route table
   * disagree, so a screen joins the sidebar in the same change that registers its route.
   */
  shipped: boolean;
};

/**
 * Every Analytics child, in sidebar order (ANL-E04-S03, report ch.6 "Where it lives"). The
 * sidebar, the `/analytics` redirect and the analytics page title all read this one list.
 */
export const ANALYTICS_CHILDREN: readonly AnalyticsChild[] = [
  { label: "Overview", path: "/analytics/overview", screenId: "SCR-1", shipped: false },
  { label: "Acquisition", path: "/analytics/acquisition", screenId: "SCR-2", shipped: false },
  { label: "Funnels", path: "/analytics/funnels", screenId: "SCR-3", shipped: false },
  { label: "Retention", path: "/analytics/retention", screenId: "SCR-4", shipped: false },
  { label: "Event explorer", path: "/analytics/events", screenId: "SCR-5", shipped: false },
  { label: "Search and discovery", path: "/analytics/search", screenId: "SCR-6", shipped: false },
  { label: "Data health", path: "/analytics/health", screenId: "SCR-7", shipped: false },
  { label: "Privacy and retention", path: "/analytics/privacy", screenId: "SCR-8", shipped: false },
  { label: "Product records", path: PRODUCT_RECORDS_PATH, shipped: true },
];

export const SHIPPED_ANALYTICS_CHILDREN: readonly AnalyticsChild[] = ANALYTICS_CHILDREN.filter(
  (child) => child.shipped,
);

/** The filters every analytics screen shares. A redirect keeps these and drops a page's own. */
export const SHARED_FILTER_PARAMS: readonly string[] = [
  "range",
  "start",
  "end",
  "compare",
  "granularity",
  "platform",
];

/**
 * Where `/analytics` lands: the first shipped child in sidebar order. Today that is Product
 * records; Overview takes over once SCR-1 ships (proposed default, ADM-OI-03).
 */
export const analyticsHomePath = (children: readonly AnalyticsChild[] = ANALYTICS_CHILDREN): string => {
  const home = children.find((child) => child.shipped);
  if (!home) throw new Error("No Analytics child has shipped, so /analytics has nowhere to land");
  return home.path;
};

/** `search` reduced to the shared filter params, in their original order ("" when none are left). */
export const keepSharedFilterParams = (search: string): string => {
  const kept = new URLSearchParams();
  for (const [name, value] of new URLSearchParams(search)) {
    if (SHARED_FILTER_PARAMS.includes(name)) kept.append(name, value);
  }
  const query = kept.toString();
  return query ? `?${query}` : "";
};

/** The Analytics child a pathname belongs to: its own route or one of its sub-routes. */
export const analyticsChildForPath = (pathname: string): AnalyticsChild | undefined =>
  ANALYTICS_CHILDREN.find((child) => pathname === child.path || pathname.startsWith(`${child.path}/`));
